import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { fetchCoffeeRecords, CoffeeRecord } from '../utils/api';
import { useSampleOrderModal } from '../App';
import { usePageMeta } from '../utils/usePageMeta';
import { Eyebrow } from '../components/Eyebrow';
import { Button } from '../components/Button';

export const Home: React.FC = () => {
  usePageMeta({
    title: 'Lot 20 Coffee — Experimental Kenyan Specialty Coffee',
    description: 'Experimental Kenyan specialty coffee from our own factories. Curious processing, anaerobic fermentation, direct trade. Request samples for your roastery.',
    canonicalPath: '/'
  });

  return (
    <>
      <HeroSection />
      <OriginStrip />
      <FeaturedCoffeesSection />
      <StorySection />
      <SamplePackCTA />
    </>
  );
};

/* ── Hero ──────────────────────────────────────────────────────────────── */

const HeroSection: React.FC = () => (
  <section className="relative min-h-[92vh] flex items-center bg-[#FAFAF8] overflow-hidden">
    <div className="relative z-10 w-full max-w-7xl mx-auto px-6 lg:px-12 py-20">
      <h1 className="font-display text-5xl sm:text-6xl md:text-7xl lg:text-8xl text-[#404040] leading-[1.02] max-w-5xl mb-8">
        Experimental,<br />Curious,<br />Non-Traditional
      </h1>
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-8 mt-12 pt-8 border-t border-[#E8E6E2]">
        <p className="font-display text-base text-[#6B6B6B] max-w-md leading-relaxed">
          Kenya's most experimental coffees — from our own factories across the western highlands.
          Pushing the boundaries of fermentation and flavor.
        </p>
        <div className="flex items-center gap-4 shrink-0">
          <Button to="/coffees" variant="primary" size="md">Explore Coffees</Button>
          <Button to="/sample-pack" variant="outline" size="md">Request Samples</Button>
        </div>
      </div>
    </div>
  </section>
);

/* ── Origin Strip ──────────────────────────────────────────────────────── */
/* Static metadata line — replaces a looping marquee ticker, which read as a
   stock SaaS-template device rather than an editorial detail. */

const ORIGIN_ITEMS = [
  'Washed', 'Natural', 'Honey', 'Anaerobic', 'Co-ferment', 'Extended Maceration',
  'Kenya AA', 'Western Highlands', 'Kericho', 'Nandi', 'Bomet',
];

const OriginStrip: React.FC = () => (
  <div className="bg-[#FAFAF8] py-5 border-y border-[#E8E6E2]">
    <div className="max-w-7xl mx-auto px-6 lg:px-12 flex flex-wrap items-center justify-center gap-x-3 gap-y-2">
      {ORIGIN_ITEMS.map((item, i) => (
        <React.Fragment key={item}>
          {i > 0 && <span className="text-[10px] text-brand-accent" aria-hidden="true">·</span>}
          <span className="text-[10px] uppercase tracking-eyebrow text-[#6B6B6B] whitespace-nowrap">
            {item}
          </span>
        </React.Fragment>
      ))}
    </div>
  </div>
);

/* ── Featured Coffees ──────────────────────────────────────────────────── */

const FeaturedCoffeesSection: React.FC = () => {
  const [coffees, setCoffees] = useState<CoffeeRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchCoffeeRecords()
      .then(records => setCoffees(records.slice(0, 3)))
      .catch(() => setCoffees([]))
      .finally(() => setIsLoading(false));
  }, []);

  if (isLoading) {
    return (
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 text-center">
          <p className="text-sm text-[#A3A3A3]">Loading...</p>
        </div>
      </section>
    );
  }

  if (coffees.length === 0) return null;

  return (
    <section className="py-24 md:py-32 bg-[#FAFAF8]">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="flex items-end justify-between mb-16 pb-6 border-b border-[#E8E6E2]">
          <div>
            <h2 className="font-display text-3xl md:text-4xl text-[#404040]">Featured Lots</h2>
            <p className="font-display text-sm text-[#A3A3A3] mt-2">This season's current offerings</p>
          </div>
          <Link
            to="/coffees"
            className="hidden sm:inline-flex text-sm text-[#6B6B6B] hover:text-[#404040] transition-colors duration-300 items-center gap-2 shrink-0 ml-8"
          >
            View all
            <span aria-hidden="true">&rarr;</span>
          </Link>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {coffees.map(coffee => (
            <CoffeeCard key={coffee.id} coffee={coffee} />
          ))}
        </div>

        <div className="sm:hidden mt-10 text-center">
          <Link to="/coffees" className="text-sm text-[#6B6B6B] hover:text-[#404040] transition-colors">
            View all coffees &rarr;
          </Link>
        </div>
      </div>
    </section>
  );
};

const CoffeeCard: React.FC<{ coffee: CoffeeRecord }> = ({ coffee }) => {
  return (
    <Link
      to={`/coffee/${coffee.slug}`}
      className="group block"
    >
      <div className="aspect-[4/5] overflow-hidden bg-[#F0EDE8] mb-5 flex items-center justify-center">
        {coffee.imageUrl ? (
          <img
            src={coffee.imageUrl}
            alt={coffee.title}
            width={800}
            height={1000}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center bg-[#F0EDE8]">
            <span className="text-4xl font-display text-[#C4BEB6]">LOT 20</span>
            <span className="text-[10px] uppercase tracking-[0.2em] text-[#C4BEB6] mt-2">{coffee.processType}</span>
          </div>
        )}
      </div>
      <div className="space-y-2">
        <p className="text-[11px] uppercase tracking-[0.15em] text-[#A3A3A3]">
          {coffee.processType}
        </p>
        <h3 className="text-xl font-display text-[#404040] group-hover:text-[#E85C27] transition-colors duration-300">
          {coffee.title}
        </h3>
        <p className="font-display text-sm text-[#6B6B6B] leading-relaxed">
          {coffee.visualDescription}
        </p>
        <div className="flex items-center gap-4 pt-1">
          {coffee.cuppingScore > 0 && (
            <span className="text-xs text-[#A3A3A3]">{coffee.cuppingScore} pts</span>
          )}
          <span className="text-xs text-[#A3A3A3]">{coffee.tastingNotes.split(',').slice(0, 3).join(', ')}</span>
        </div>
      </div>
    </Link>
  );
};

/* ── Story Section ─────────────────────────────────────────────────────── */

const StorySection: React.FC = () => (
  <section className="py-24 md:py-32 bg-white">
    <div className="max-w-7xl mx-auto px-6 lg:px-12">
      <div className="grid md:grid-cols-2 gap-16 lg:gap-24 items-center">
        <div>
          <Eyebrow className="mb-3">Our Story</Eyebrow>
          <h2 className="font-display text-3xl md:text-4xl text-[#404040] mb-6">
            Farmers, processors, and innovators
          </h2>
          <p className="font-display text-[#6B6B6B] leading-relaxed mb-6">
            Working at origin across Kericho, Nandi, Bomet, and the greater South Nyanza
            region, we control every step — from cherry selection through experimental
            fermentation to final drying.
          </p>
          <p className="font-display text-[#6B6B6B] leading-relaxed mb-8">
            Every lot is an experiment. We work with diverse yeast cultures, extended
            anaerobic fermentation, and unconventional drying methods to unlock
            flavors that push what Kenyan coffee can be.
          </p>
          <Link
            to="/about"
            className="inline-flex items-center text-sm text-[#404040] font-medium hover:text-[#E85C27] transition-colors duration-300 gap-2"
          >
            Read our full story
            <span aria-hidden="true">&rarr;</span>
          </Link>
        </div>
        {/* Stats — editorial pull-numbers, not dashboard tiles */}
        <div className="grid grid-cols-2 gap-x-8 gap-y-10">
          {[
            { value: '805', label: 'Registered farmers', sub: 'across 4 counties' },
            { value: '4+', label: 'Processing factories', sub: 'western highlands' },
            { value: '85–96', label: 'Cupping score range', sub: 'SCA protocol' },
            { value: '100%', label: 'Origin controlled', sub: 'farm to export' },
          ].map(({ value, label, sub }) => (
            <div key={label} className="border-l-2 border-[#E85C27]/30 pl-6">
              <p className="font-display text-4xl md:text-5xl text-[#404040] mb-1">{value}</p>
              <p className="text-sm font-medium text-[#404040]">{label}</p>
              <p className="text-xs text-[#A3A3A3] mt-0.5">{sub}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

/* ── Sample Pack CTA ───────────────────────────────────────────────────── */

const SamplePackCTA: React.FC = () => (
  <section className="py-24 md:py-32 bg-[#404040]">
    <div className="max-w-3xl mx-auto px-6 lg:px-12 text-center">
      <h2 className="font-display text-3xl md:text-4xl text-white mb-6">
        <span className="text-brand-accent">For roasters —</span> request our sample set
      </h2>
      <p className="font-display text-[#A3A3A3] leading-relaxed mb-10 max-w-xl mx-auto">
        One request gets you every coffee in our current lineup. Free shipping to the UK & EU;
        $60 USD via DHL everywhere else, samples plus a phytosanitary certificate.
      </p>
      <div className="flex flex-wrap justify-center gap-4">
        <Button to="/sample-pack" variant="primary" size="md">Request Samples</Button>
        <Button to="/coffees" variant="outline-light" size="md">Browse Coffees</Button>
      </div>
    </div>
  </section>
);
